import React, { useState } from "react";
import { connect } from "react-redux";
import { Button, Card } from "antd";
import { FaArrowLeft, FaCircleCheck } from "react-icons/fa6";

import EmployeeProgressCard from "./EmployeeProgressCard";
import EmployeeScoreCard from "./EmployeeScoreCard";
import HeaderComponent from "components/card/HeaderComponent";
import ReviewScore from "./ReviewScore";
import WrittenFeedback from "./WrittenFeedback";
import RatingReference from "./RatingReference";
import {
  Users as LucideUsers,
  Target as LucideTarget,
  TrendingUp as LucideTrendingUp,
} from "lucide-react";
import { color } from "@chakra-ui/system";

const StartReviewForm = ({ employee }) => {
  const { name = "", role = "", department = "", employeeId = "" } = employee || {};
  const [ratings, setRatings] = useState({});

  const handleRatingChange = (category, value) =>
    setRatings((prev) => ({ ...prev, [category]: value }));

  const pointsMap = { 1: 25, 2: 50, 3: 75, 4: 100 };
  const earnedPoints = Object.values(ratings).reduce(
    (sum, rating) => sum + (pointsMap[rating] || 0),
    0
  );
  

  const completed = Object.keys(ratings).length;
  const total = 8;

  const reviewSections = [
    {
      title: "Our People",
      borderClass: "border-emerald-200",
      icon: <LucideUsers size={20}/>,
      colorTheme: "dark-green",
      description: "How we connect, collaborate, and contribute to a positive team culture.",
      bgcolor: "bg-emerald-50/5",
      categories: [
        { category: "Attitude", descriptions: [
          "Consistently goes above expectations and contributes exceptional value.",
          "Delivers work that fully meets role expectations.",
          "Sometimes meets expectations but requires guidance.",
          "Frequently fails to deliver expected outcomes."
        ] },
        { category: "Communication", descriptions: [
          "Communicates clearly and keeps everyone in the loop.",
          "Shares updates on time and communicates respectfully.",
          "Sometimes unclear or slow to communicate.",
          "Poor communication creates confusion or delays."
        ] },
        { category: "Initiative", descriptions: [
          "Spots opportunities and takes ownership without being asked.",
          "Manages tasks well and shows initiative when needed.",
          "Relies on direction; could take more ownership.",
          "Avoids responsibility or follow-through."
        ] },
        { category: "Reliability", descriptions: [
          "Can always be counted on; consistent, punctual, and dependable.",
          "Generally reliable and delivers on commitments.",
          "Occasionally late or inconsistent.",
          "Frequently unreliable or unresponsive."
        ] }
      ]
    },
    {
      title: "Our Service",
      borderClass: "border-blue-200", 
      icon: <LucideTarget size={20}/>,
      colorTheme: "dark-blue",
      description: "How we deliver results for our clients.",
      bgcolor: "bg-blue-50/50",
      categories: [
        { category: "Delivery Excellence", descriptions: [
          "Consistently exceeds client expectations and timelines.",
          "Delivers quality work on time and meets client needs.",
          "Sometimes misses deadlines or requires follow-up.",
          "Regularly fails to meet commitments."
        ] },
        { category: "Quality of Work", descriptions: [
          "Work is exceptional; minimal errors and high attention to detail.",
          "Produces quality work with acceptable accuracy.",
          "Occasional quality issues that need correction.",
          "Frequent errors or substandard output."
        ] }
      ]
    },
    {
      title: "Our Growth",
      borderClass: "border-purple-200",
      icon: <LucideTrendingUp size={20}/>,
      colorTheme: "dark-purple",
      description: "How we learn, adapt, and move forward.",
      bgcolor: "bg-pink-50/60",

      categories: [
        { category: "Adaptability & Learning", descriptions: [
          "Embraces change and actively seeks to learn and improve.",
          "Adapts to change and is open to new approaches.",
          "Hesitant to change; needs encouragement.",
          "Resists change or shows no interest in growth."
        ] },
        { category: "Problem Solving & Innovation", descriptions: [
          "Brings creative solutions and innovative thinking.",
          "Solves problems effectively with sound judgment.",
          "Requires guidance when facing challenges.",
          "Struggles to address problems independently."
        ] }
      ]
    }
  ];

  return (
    <Card className="min-h-screen gradient pink">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-6">

        <div className="inline-flex items-center gap-2 px-3">
          <FaArrowLeft />
          <a href="/console/performance-reviews" className="flex">Back to Reviews</a>
        </div>

        <EmployeeProgressCard
          name={name}
          role={role}
          department={department}
          employeeId={employeeId}
          completed={completed}
          total={total}
        />

        {completed === total && (
          <EmployeeScoreCard earnedPoints={earnedPoints} grandTotal={total * 100} />
        )}

        <RatingReference />

        {reviewSections.map((section) => (
          <div key={section.title} className={`border-2 rounded-xl ${section.borderClass}`}>
            <HeaderComponent
              title={section.title}
              description={section.description}
              bgcolor={section.bgcolor}
              icon={section.icon}
              colorTheme={section.colorTheme}
            >
              {section.categories.map(({ category, descriptions }) => (
                <ReviewScore
                  key={category}
                  category={category}
                  descriptions={descriptions}
                  value={ratings[category] || null}
                  onChange={handleRatingChange}
                />
              ))}
            </HeaderComponent>
          </div>
        ))}

        <div className="border-2 rounded-xl border-slate-200">
          <HeaderComponent
            title="Written Feedback"
            description="Provide detailed feedback and set goals for the next period"
            bgcolor="purple"
          >
            <WrittenFeedback />
          </HeaderComponent>
        </div>

        <Card className="gap-6 rounded-xl border-2 border-purple-200 shadow-lg">
          <div className="flex justify-between items-center">
            <p className="text-[13px]">Please complete all sections before submitting</p>
            <div className="flex gap-2">
              <a href="/console/performance-reviews" className="border-2 rounded-xl bg-white text-gray-800 p-2 text-sm">
                Cancel
              </a>
              <Button
                icon={<FaCircleCheck />}
                className="btn-gradient-emerald text-sm hover hover:text-white rounded-xl pb-2"
                size="large"
                disabled={completed !== total}
              >
                Submit Review
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Card>
  );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(StartReviewForm);
